import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Card } from "@/components/ui";

import { PlayerPost, ReviewStatus } from "@/interfaces";
import { createVideoThumbnailBlob, printBasicDate } from "@/utils";
import { PostServices } from "@/services";

import { PostFailedModal } from "../PostFailedModal";
import { ComplianceModal } from "../ComplianceModal";

import useDisplayData from "./hooks";
import { TriangleAlert } from "lucide-react";

interface OwnProps {
  post: PlayerPost;
  refreshHandler: () => void;
}
type Props = OwnProps & RouteComponentProps;

const NeedsAttentionCardFC: React.FC<Props> = ({ post, refreshHandler }) => {
  const { hasPostMessage, createdDate, mediaType, mediaUrl, socialErrorMessage } = useDisplayData(post);
  const [thumbnail, setThumbnail] = React.useState<string | undefined>();
  const [showPostFailedModal, setShowPostFailedModal] = React.useState<boolean>(false);
  const [showComplianceModal, setShowComplianceModal] = React.useState<boolean>(false);

  const [selectedPost, setSelectedPost] = React.useState<PlayerPost>(post);

  React.useEffect(() => {
    if (mediaType) {
      const creativeUrl = mediaUrl;
      const urlPromise = mediaType === "IMAGE" ? Promise.resolve(creativeUrl) : createVideoThumbnailBlob(creativeUrl);
      urlPromise.then(url => {
        setThumbnail(url);
      });
    }
  }, [mediaUrl]);

  const cardPressed = (): void => {
    PostServices.getById(post.postId).then(response => {
      setSelectedPost(response);
      // @ts-ignore
      if (
        post.coachPostReviewStatus === ReviewStatus.REQUEST_CHANGES ||
        post.reviewStatus === ReviewStatus.REQUEST_CHANGES
      ) {
        setShowComplianceModal(true);
      } else {
        setShowPostFailedModal(true);
      }
    });
  };

  return (
    <>
      <Card
        onClick={cardPressed}
        className="w-[220px] h-[220px] rounded flex flex-col justify-between overflow-hidden cursor-pointer border border-red-100 bg-white hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 select-none group/card shrink-0 p-0"
      >
        <div className="flex flex-col flex-1 min-h-0">
          {/* Media Thumbnail Container (no top, left, or right padding - sits flush against the top rounded corner of the card!) */}
          <div className="relative flex-1 w-full bg-slate-50 shrink-0 border-b border-slate-100 overflow-hidden rounded-t">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt="Post thumbnail"
                className="size-full object-cover transition-transform duration-200 group-hover/card:scale-105"
              />
            ) : (
              <div className="size-full flex items-center justify-center text-slate-300">
                <TriangleAlert className="size-5 stroke-[1.5]" />
              </div>
            )}
          </div>

          {/* Content Body (very tight padding and gap to prevent too much room for title/subtitle) */}
          <div className="p-2.5 pt-2 flex flex-col min-h-0 justify-between gap-1">
            <h4
              className="text-sm font-bold text-slate-800 leading-tight line-clamp-2 text-left"
              title={socialErrorMessage}
            >
              {socialErrorMessage || "Failed to post"}
            </h4>
            <p className="text-xs text-slate-400 font-medium select-none text-left leading-none">
              {printBasicDate(createdDate)}
            </p>
          </div>
        </div>
      </Card>

      {showPostFailedModal && (
        <PostFailedModal
          opened={showPostFailedModal}
          thumbnail={thumbnail || ""}
          selectedPost={selectedPost}
          closeHandler={() => {
            setShowPostFailedModal(false);
            refreshHandler();
          }}
        />
      )}
      {showComplianceModal && (
        <ComplianceModal
          playerId={post.playerId}
          opened={showComplianceModal}
          thumbnail={thumbnail || ""}
          selectedPost={selectedPost}
          closeHandler={() => {
            setShowComplianceModal(false);
            refreshHandler();
          }}
        />
      )}
    </>
  );
};

export const NeedsAttentionCard = withRouter(NeedsAttentionCardFC);
